import _ from "lodash";
import {
  createResource,
  deleteResource,
  determinePaginationParams,
  determineSelector,
  getResource,
  readUrl,
  readUrlObjectBased,
  updateResource,
  updateResourcePath,
} from "./api";

const getAll = async (options = {}) => {
  const defaultOptions = { page: 1, perPage: 12, query: null, order: "name" };
  const { exclude, query, page, perPage, ...otherOptions } = { ...defaultOptions, ...options };
  const paginationOptions = determinePaginationParams({ page, per_page: perPage, returnType: "object" });

  let selector = { ...otherOptions, ...paginationOptions };

  if (!_.isEmpty(query)) {
    selector = { ...selector, name: `ilike.*${query}*` };
  }

  if (!_.isEmpty(exclude)) {
    selector = { ...selector, id: `not.in.(${exclude.join(",")})` };
  }

  const url = readUrl("experts", { selector: new URLSearchParams(selector).toString() });
  return getResource(url);
};

const get = async (id) => {
  const selector = determineSelector(id, { keyId: "id", keySlug: "slug" });
  const url = readUrl("experts", { selector });
  return getResource(url);
};

// RLS drops any of these ids the viewer isn't entitled to see, so the returned id set is also the
// answer to "which of these experts can this viewer open?". `select` narrows the returned columns for
// callers that only need that answer. Omit it to get every column, as the display consumers do.
const getAllById = async (ids, options = { page: 1, per_page: 10 }) => {
  const idQueryString = `in.(${ids.join(",")})`;
  const { select, ...rest } = options;
  const url = readUrlObjectBased("experts", {
    conditionParams: { id: idQueryString, ...(select && { select }) },
    ...rest,
  });
  return getResource(url);
};

const getAllRepresentedByPr = async (prProfileId, fields = ["*"]) => {
  const url = readUrl("experts", {
    selector: new URLSearchParams({
      representative_profile_ids: `cs.${prProfileId}`,
      select: fields.join(","),
      order: "name",
    }).toString(),
  });
  return getResource(url);
};

// Pitcher locations for the pitch inbox: an expert/hybrid pitcher's location lives on
// their self-represented expert entity (profiles themselves carry no location).
// One PATCH carrying every editable entity attribute (name, description, headline_custom). The server
// applies them in a single transaction, so this is all-or-nothing, and the success body is the
// re-selected experts view row rather than an echo of what was sent.
//
// Like companies.updateById this resolves the raw `{ data }` / `{ error }` shape rather than throwing,
// so callers must run it through throwOnApiError before unwrapping.
const updateById = async (id, attributes) => {
  const url = updateResourcePath("experts", id);
  return updateResource(url, { data: { attributes } });
};

// The profile-image trio mirrors products.js/companies.js exactly — same nested `profile_image`
// route, same `{ data } | { error }` result — because <ProfileImage> routes through whichever of
// them owns the entity it was handed and can only do that if they answer alike.
const profileImagePath = (expertId) => `${updateResourcePath("experts", expertId)}/profile_image`;

const createProfileImageById = async (expertId, attributes) => {
  return createResource(profileImagePath(expertId), { data: { attributes } });
};

const updateProfileImageById = async (expertId, attributes) => {
  return updateResource(profileImagePath(expertId), { data: { attributes } });
};

const deleteProfileImageById = async (expertId) => {
  return deleteResource(profileImagePath(expertId));
};

export default {
  createProfileImageById,
  deleteProfileImageById,
  get,
  getAll,
  getAllById,
  getAllRepresentedByPr,
  updateById,
  updateProfileImageById,
};
